import { Text, View } from '@react-pdf/renderer';

import type {
  SentimentScore,
  SentimentScoresTopCompetitor,
} from '../../../../api/generated/insights.schemas';
import { palette, styles } from '../styles';

type Sentiment = SentimentScore | SentimentScoresTopCompetitor | null | undefined;

interface SentimentSectionProps {
  ownSentiment: Sentiment;
  competitorSentiment: Sentiment;
}

interface BlockProps {
  title: string;
  data: Sentiment;
  isLast?: boolean;
}

function SentimentBlock({ title, data, isLast }: BlockProps) {
  const containerStyle = isLast
    ? [styles.sentimentBlock, styles.sentimentBlockLast]
    : [styles.sentimentBlock];

  if (!data) {
    return (
      <View style={containerStyle}>
        <Text style={styles.sentimentTitle}>{title}</Text>
        <Text style={styles.sentimentBrand}>
          The selected AI platform returned no sentiment data.
        </Text>
      </View>
    );
  }

  const positivityPctRaw = data.positivityPercentage?.value;
  const positivityPct =
    typeof positivityPctRaw === 'number'
      ? Math.max(0, Math.min(100, positivityPctRaw))
      : null;

  const positiveKeywords = (data.keywords?.positive ?? []).filter(Boolean);
  const negativeKeywords = (data.keywords?.negative ?? []).filter(Boolean);

  return (
    <View style={containerStyle}>
      <Text style={styles.sentimentTitle}>{title}</Text>
      {data.brandName ? (
        <Text style={styles.sentimentBrand}>{data.brandName}</Text>
      ) : null}

      {positivityPct !== null ? (
        <>
          <Text style={styles.sentimentScore}>{positivityPct}% positive</Text>
          <View style={styles.sentimentBarTrack}>
            <View
              style={[
                styles.sentimentBarFill,
                { width: `${positivityPct}%` },
              ]}
            />
          </View>
        </>
      ) : (
        <Text style={styles.sentimentBrand}>No score available.</Text>
      )}

      {positiveKeywords.length > 0 && (
        <View style={styles.keywordGroup}>
          <Text style={styles.keywordGroupTitle}>Positive keywords</Text>
          <View style={styles.keywordRow}>
            {positiveKeywords.map((kw, idx) => (
              <Text
                key={`${kw}-${idx}`}
                style={[
                  styles.keywordBadge,
                  {
                    backgroundColor: palette.greenBg,
                    color: palette.greenText,
                  },
                ]}
              >
                {kw}
              </Text>
            ))}
          </View>
        </View>
      )}

      {negativeKeywords.length > 0 && (
        <View style={styles.keywordGroup}>
          <Text style={styles.keywordGroupTitle}>Negative keywords</Text>
          <View style={styles.keywordRow}>
            {negativeKeywords.map((kw, idx) => (
              <Text
                key={`${kw}-${idx}`}
                style={[
                  styles.keywordBadge,
                  {
                    backgroundColor: palette.redBg,
                    color: palette.redText,
                  },
                ]}
              >
                {kw}
              </Text>
            ))}
          </View>
        </View>
      )}
    </View>
  );
}

export default function SentimentSection({
  ownSentiment,
  competitorSentiment,
}: SentimentSectionProps) {
  return (
    <View>
      <Text style={styles.sectionTitle}>Sentiment</Text>
      <Text style={styles.sectionSubtitle}>
        How positively, neutrally or negatively the brand and the top competitor
        are mentioned in the AI-generated answers.
      </Text>

      <SentimentBlock title="Brand sentiment" data={ownSentiment} />
      <SentimentBlock
        title="Top competitor sentiment"
        data={competitorSentiment}
        isLast
      />
    </View>
  );
}
